import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CRMCompanyStorm } from '../../../models/crm/CRMCompanyStorm';
import { CRMStatus, CRMStatusLabels, INVESTMENT_MADE_STATUSES } from '../../../models/crm/CRMStatus';
import { CourtshipTypeEnum } from '../../../enums/courtship-type.enum';
import { CRMItem } from '../../../models/crm/CRMItem.model';
import { NgForm } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { CrmStatusService } from '../../../../core/data-services/crm-status.service';
import { EventsHubService } from '../../../../core/events-hub.service';
import { AuthenticationService, DateTimeService, RmsUtilsService } from '../../../../core/services';
import { DateConverterService } from '../../../../core/data-services/date-converter.service';
import { Domain, DomainRelevance } from '../../../models/domain.model';
import { CompanyBasic, TextContent, User } from '../../../models';
import { CRMAlarm } from '../../../models/crm/CRMAlarm.model';
import { CrmAlarmUtilsService } from '../../../../core/data-services/crm-alarm-utils.service';
import { CompanyService } from '../../../../core/data-services/company';
import { CrmForm } from '../crm-form';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CrmService } from '../../../../core/data-services/crm.service';
import { DomainService } from '../../../../core/data-services/domain.service';
import { BannerNotificationsService } from '../../../../xform-compat';
import { TranslateService } from '@ngx-translate/core';
import { CrmIntegrationService } from '../../../../core/services/crm-integration.service';
import { DateTime } from 'luxon';
import { CONFIG } from '../../../../config';

@Component({
    selector: 'rms-crm-standard-form',
    templateUrl: './crm-standard-form.component.html',
    styleUrls: ['./crm-standard-form.component.scss'],
    standalone: false
})
export class CrmStandardFormComponent implements OnInit, CrmForm {

  @Input() editMode: boolean;
  @Input() inputCrmItem: CRMItem;
  @Input() inputCompanyId: number;
  @Input() companyList: Array<CompanyBasic> = new Array<CompanyBasic>();

  @Output() formSubmitted = new EventEmitter<any>();

  @ViewChild('addCrmForm', { static: true }) form: NgForm;

  public item: CRMItem = new CRMItem();
  public isCrmProject = false;
  public silenceUntilDate: DateTime;
  public note: TextContent = new TextContent();
  public companyTypeahead = new Subject<string>();
  public companySearching = false;
  public companyError = '';
  public showMore = false;
  public availableStatuses = [];

  public readonly storm = CRMCompanyStorm;
  public readonly crmStatus = CRMStatus;
  public readonly courtship = CourtshipTypeEnum;
  public readonly INVESTMENT_MADE_STATUSES = INVESTMENT_MADE_STATUSES;
  public readonly statusLabels = CRMStatusLabels;
  public readonly curDate = DateTime.now();

  private _origItem: CRMItem;

  constructor(
    private crmService: CrmService,
    public crmStatusService: CrmStatusService,
    private eventsHubService: EventsHubService,
    private crmDateConverterService: DateConverterService,
    private crmAlarmUtilsService: CrmAlarmUtilsService,
    private authenticationService: AuthenticationService,
    private companyService: CompanyService,
    private cd: ChangeDetectorRef,
    private companyDomainService: DomainService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService,
    private crmIntegrationService: CrmIntegrationService
  ) { }

  ngOnInit() {
    this.isCrmProject = this.editMode && this.inputCrmItem.companies.length === 0;

    this.companyAutocomplete();
    this.initializeItem();
    this._origItem = RmsUtilsService.deepClone(this.item);
    this.setIsClean();
    this.updateAvailableStatuses();
  }

  public submit() {
    if (this.cannotSubmit()) {
      return;
    }

    const item = this.getCrmData();
    if (this.editMode || item.isCrmProject) {
      this.formSubmitted.next(item);
    } else {
      this.crmService.validateCrmItem(item).subscribe(() => {
        this.formSubmitted.next(item);
        this.addDomainToCompany(item);
      }, error => {
        this.companyError = this.translateService.instant(error);
      });
    }
  }

  public addCompany() {
    this.eventsHubService.openAddCompanyModal(false);
  }

  public updateSilenceUntil(value: string): void {
    if (!this.silenceUntilDate && this.item.companies[0].etaki) {
      this.silenceUntilDate = this.crmDateConverterService.specificDate(value);
    }
  }

  public showSilenceUntil(): boolean {
    return this.crmStatusService.isSilenceStatus(this.item.status);
  }

  public clearSilence() {
    this.silenceUntilDate = null;
  }

  public handleCrmTypeChange() {
    this.initializeItem();
    this.item.status = CRMStatus.RESEARCH;
    this.updateAvailableStatuses();
  }

  public handleCompanyChange(selectedCompany) {
    if (this.companyHasSingleDomain(selectedCompany)) {
      this.item.domains = selectedCompany.domains;
    }
  }

  public updateEnfusionId(event, idx) {
    this.item.externalIds[idx] = { externalId: event.target.value, idType: 'ENFUSION'};
  }

  public cannotSubmit() {
    if (this.isCrmProject) {
      return this.form.invalid || this.notAssigned() || this.noStatus() || this.noDomains();
    }
    return this.form.invalid || this.notAssigned() || !this.validEtaki() || !this.validEtakiDates() || this.noStatus() || this.noDomains() ||
      this.noStorm();
  }

  public setIsClean() {
    this.crmIntegrationService.isClean = () => this.isClean(this.item, this._origItem);
  }

  private isClean(item: CRMItem, origItem: CRMItem) {
    return item.name === origItem.name &&
      (item.companies[0].company && item.companies[0].company.id) ===
      (origItem.companies[0].company && origItem.companies[0].company.id) &&
      item.status === origItem.status &&
      item.companies[0].courtshipType === origItem.companies[0].courtshipType &&
      item.companies[0].etaki === origItem.companies[0].etaki &&
      item.leader.id === origItem.leader.id &&
      (item.triageOwner && item.triageOwner.id) === (origItem.triageOwner && origItem.triageOwner.id) &&
      (item.domains[0] && item.domains[0].id) === (origItem.domains[0] && origItem.domains[0].id) &&
      item.participants.every(p => origItem.participants.find(op => op.user.id === p.user.id));
  }

  private notAssigned() {
    return !this.item.leader;
  }

  private noStatus() {
    return !this.item.status;
  }

  private noDomains() {
    return !this.item.domains || this.item.domains.length === 0;
  }

  private noStorm() {
    return this.item.companies && this.item.companies[0] && (this.item.companies[0].company.isPrivate && !this.item.companies[0].storm);
  }

  private validEtaki(): boolean {
    if (this.item.companies && this.item.companies[0] && this.item.companies[0].etaki) {
      return this.crmDateConverterService.isValidDateFormat(this.item.companies[0].etaki);
    }
    return true;
  }

  private validEtakiDates(): boolean {
    const formValue = this.form.form.value;
    if (this.crmStatusService.hasEtakiStatus(formValue)) {
      return !!(formValue.etaki && this.silenceUntilDate);
    }
    return true;
  }

  private initializeItem() {
    if (this.inputCrmItem) {
      this.item.id = this.inputCrmItem.id;
      this.item.name = this.inputCrmItem.name;
      this.item.status = this.inputCrmItem.status;
      this.item.description = this.inputCrmItem.description;
      this.item.domains = [...this.inputCrmItem.domains];
      this.item.leader = this.inputCrmItem.leader;
      this.item.triageOwner = this.inputCrmItem.triageOwner;
      this.item.participants = [...this.inputCrmItem.participants];
      this.item.indicated = this.inputCrmItem.indicated;
      this.item.received = this.inputCrmItem.received;
      this.item.externalIds = [...this.inputCrmItem.externalIds];
      this.item.companies = this.inputCrmItem.companies && this.inputCrmItem.companies.length > 0 ? [{
        ...this.inputCrmItem.companies[0],
        company: {...this.inputCrmItem.companies[0].company}
      }] : [];
      this.item.alarms = [...this.inputCrmItem.alarms];
      const silenceAlarm = this.crmAlarmUtilsService.findSilenceAlarm(this.inputCrmItem);
      this.silenceUntilDate = silenceAlarm ? DateTimeService.parseDate(silenceAlarm.trigger.value) : null;
    } else {
      this.item.id = null;
      this.item.name = null;
      this.item.status = CRMStatus.RESEARCH;
      this.item.description = null;
      this.item.domains = new Array<Domain>();
      this.item.participants = new Array<{updated: Date, created: Date, user: User, confirmed: boolean, leader: boolean, triageOwner: boolean}>();
      this.silenceUntilDate = null;
      this.item.alarms = new Array<CRMAlarm>();
      this.item.externalIds = [];
      this.item.leader = {
        updated: new Date(),
        created: new Date(),
        user: this.authenticationService.getLoggedUser(),
        confirmed: true
      };
      this.item.triageOwner = null;

      this.item.companies = [{company: null, storm: null, etaki: null}];
      if (this.inputCompanyId) {
        this.companyService.getCompany(this.inputCompanyId, {extrafield: 'domains'}).subscribe((resp) => {
          const newCrmCompany: any = new CompanyBasic(resp);
          newCrmCompany['updated'] = new Date();
          newCrmCompany['created'] = new Date();
          newCrmCompany['createdBy'] = this.authenticationService.getLoggedUser();
          newCrmCompany['updatedBy'] = this.authenticationService.getLoggedUser();

          this.item.companies = [{company: newCrmCompany, storm: CRMCompanyStorm.NONE, etaki: ''}];
          if (this.companyHasSingleDomain(resp)) {
            this.item.domains = resp.domains;
          }
        });
      }

      this._origItem = RmsUtilsService.deepClone(this.item);
    }
  }

  private getCrmData() {
    const isSilencedStatus = this.crmStatusService.isSilenceStatus(this.item.status) && this.silenceUntilDate;
    const leaderId = (this.item.leader && this.item.leader.id) ||
      (this.item.leader && this.item.leader.user && this.item.leader.user.id);
    const triageOwnerId =  (this.item.triageOwner && this.item.triageOwner.id) ||
      (this.item.triageOwner && this.item.triageOwner.user && this.item.triageOwner.user.id);
    return {
      id: this.item.id,
      name: this.item.name,
      status: isSilencedStatus && this.item.status !== CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION ? CRMStatus.SILENCED : this.item.status,
      description: this.item.description,
      domainIds: [...this.item.domains.map(g => g.id)],
      leaderId: leaderId || (leaderId === 0 ? 0 : null),
      triageOwnerId: triageOwnerId || (triageOwnerId === 0 ? 0 : null),
      indicated: this.item.indicated,
      received: this.item.received,
      externalIds: this.item.externalIds.filter(d => d.externalId && d.idType),
      ownerIds: [this.item.leader, ...this.item.participants].map(o => {
        if (o['id'] === 0) {
          return 0;
        }
        const co = o as any;
        return co.id || co.user.id;
      }),
      companies: this.isCrmProject ? null : [... this.item.companies.map(c => {
        const cc = c as any;
        let stormVal = cc.storm;
        if ((!stormVal || stormVal === CRMCompanyStorm.NONE) && !cc.company.isPrivate) {
          stormVal = CRMCompanyStorm.PUBLIC;
        }
        return {
          companyId: cc.id || cc.company.id,
          etaki: cc.etaki,
          storm: stormVal,
          courtshipType: cc.company.courtshipType
        };
      })],
      notes: this.note.content ? [this.note] : [],
      alarms: this.item.alarms || [],
      silenceUntil: this.silenceUntilDate,
      isCrmProject: this.isCrmProject
    };
  }

  private companyAutocomplete() {
    this.companyTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => {
        this.companySearching = true;
        return this.crmService.searchCompanies({ q: term });
      }),
    ).subscribe((value: any) => {
      this.companySearching = false;
      this.companyList = value;
      this.cd.markForCheck();
    }, () => {
      this.companySearching = false;
      this.companyList = new Array<CompanyBasic>();
    });
  }

  private addDomainToCompany(item) {
    const companyId = item.companies[0].companyId;
    const domainId = item.domainIds[0];
    if (companyId && domainId) {
      this.companyDomainService.addCompanyToDomain(companyId, domainId).subscribe(() => {}, error => {
        this.bannerNotificationService.error('Could not add domain to company');
      });
    }
  }

  private companyHasSingleDomain(selectedCompany): boolean {
    return selectedCompany && selectedCompany.domains && selectedCompany.domains.length === 1 &&
        selectedCompany.domains[0].relevance === DomainRelevance.DEFAULT;
  }

  private updateAvailableStatuses() {
    this.availableStatuses = this.crmStatusService.getStatusDropdownOptions(this.editMode ? this._origItem : null, this.isCrmProject);
  }
}
